import { useAxios } from './useAxios'
import { getBackendErrorCode } from './useAxiosError'
import type { Notify } from '@/types/store'
import { z } from 'zod'
import { PaymentInterval } from '@/types/purchase/PaymentInterval'
import { BillingPeriodOption } from '@/types/purchase/BillingPeriod'

/**
 * Why mail on a domain is switched off. `no_entitlement` and
 * `domain_suspended` are lifted automatically once the domain is entitled
 * again; `customer` needs the owner to switch mail back on; `domain_deleted`
 * needs the domain restored first; `admin` only support can lift.
 */
export const MailDisabledReason = z.enum([
  'no_entitlement',
  'domain_suspended',
  'customer',
  'domain_deleted',
  'admin'
])

export type MailDisabledReason = z.infer<typeof MailDisabledReason>

export const MailBillingStatus = z.object({
  entitlement: z.enum(['paid', 'hosting_bundle', 'grandfathered', 'none']),
  base_subscription_id: z.string().nullish(),
  base_price_gross_cents: z.number(),
  storage_used_bytes: z.number(),
  storage_included_bytes: z.number(),
  storage_overage_bytes: z.number(),
  outbound_count: z.number(),
  outbound_included: z.number(),
  outbound_overage: z.number(),
  projected_overage_net_cents: z.number(),
  /** Paid-through date (Unix seconds) of a plan cancelled at period end. */
  access_until: z.number().nullish(),
  /** A hosting package now covers the domain and the paid plan is about to end with a pro-rata refund. */
  takeover_pending: z.boolean().optional(),
  mail_disabled_reason: MailDisabledReason.nullish(),
  /** The domain's mail plan, whoever pays it; null without one. */
  subscription_id: z.number().int().nullish(),
  billing_freq: PaymentInterval.nullish(),
  /** One cycle of the paid plan at its billing_freq, on the account's basis; null without a paid plan. */
  plan_price_cents: z.number().int().nullish(),
  /** The periods activation can book, on the account's basis; absent from an older backend. */
  plan_options: z.array(BillingPeriodOption).optional(),
  /** Unix seconds. */
  renews_at: z.number().nullish(),
  /** Periods the plan can change to; empty unless the caller pays the plan on Stripe. */
  billing_periods: z.array(PaymentInterval).nullish(),
  pending_billing_freq: PaymentInterval.nullish(),
  /** Unix seconds. */
  pending_from: z.number().nullish()
})

export type MailBillingStatus = z.infer<typeof MailBillingStatus>

export interface ActivateResponse {
  status: string
  entitlement: string
  message: string
}

/**
 * Who sees a mail-entitlement refusal decides its wording: the domain owner
 * can switch mail on, a delegate or mailbox user can only ask the owner.
 */
export type MailEntitlementAudience = 'owner' | 'delegate'

const MAIL_ENTITLEMENT_ERROR_KEYS: Record<string, string> = {
  mail_not_entitled: 'notEntitled',
  mail_disabled: 'disabled',
  mail_admin_hold: 'adminHold',
  mail_domain_unverified: 'unverified'
}

/**
 * i18n key for the codes the mail create and re-activate endpoints return
 * when the domain has no mail entitlement (`mail_not_entitled`), mail was
 * switched off on it (`mail_disabled`), support has put it on hold
 * (`mail_admin_hold`), or the domain is registered elsewhere and not yet
 * verified (`mail_domain_unverified`). Undefined for any other code.
 */
export function mailEntitlementErrorKey(
  code: string | undefined,
  audience: MailEntitlementAudience
): string | undefined {
  const leaf = code ? MAIL_ENTITLEMENT_ERROR_KEYS[code] : undefined
  return leaf ? `dashboard.mail.entitlement.${audience}.${leaf}` : undefined
}

/**
 * Statuses of a mail-entitlement refusal to keep out of Sentry. The 403s are
 * silent by default; `mail_disabled` is a 409, an expected outcome rather than
 * a fault. Pass as `silentStatuses` to handleAxiosError.
 */
export function mailEntitlementSilentStatuses(error: unknown): number[] {
  return mailEntitlementErrorKey(getBackendErrorCode(error), 'owner') ? [409] : []
}

/**
 * Route to the domain's mail activation (the E-Mail domain tab with the domain
 * preselected). A mail subdomain resolves to its parent there.
 */
export function mailActivationRoute(domain: string): string {
  return `/dashboard/email/domain?domain=${encodeURIComponent(domain)}`
}

/**
 * The entry of `domains` a `?domain=` link means: the domain itself, or for a
 * mail subdomain (lists.example.com) the closest parent, whose settings manage
 * it. Case-insensitive.
 */
export function matchMailDomain<T extends { name: string }>(domains: T[], wanted: string): T | undefined {
  const name = wanted.toLowerCase()
  let best: T | undefined
  for (const d of domains) {
    const candidate = d.name.toLowerCase()
    if (candidate !== name && !name.endsWith(`.${candidate}`)) continue
    if (!best || candidate.length > best.name.length) best = d
  }
  return best
}

/**
 * Notification text for a mail-entitlement refusal, or undefined when the
 * error is something else. Spread it over the call site's own error notify so
 * its generic message stays the fallback. Passing `activationDomain` adds a
 * link to that domain's mail activation, which only the owner can use.
 */
export function mailEntitlementNotice(
  t: (key: string) => string,
  error: unknown,
  audience: MailEntitlementAudience,
  activationDomain?: string
): Pick<Notify, 'message' | 'action'> | undefined {
  const key = mailEntitlementErrorKey(getBackendErrorCode(error), audience)
  if (!key) return undefined
  const message = t(key)
  if (!activationDomain) return { message }
  return {
    message,
    action: {
      label: t('dashboard.mail.entitlement.goToActivation'),
      route: mailActivationRoute(activationDomain)
    }
  }
}

const CART_REFUSAL_KEYS: Record<string, string> = {
  mail_already_subscribed: 'cart.mailErrors.alreadySubscribed',
  mail_included_in_hosting: 'cart.mailErrors.includedInHosting',
  mail_admin_hold: 'cart.mailErrors.adminHold',
  domain_suspended: 'cart.mailErrors.domainSuspended',
  domain_deleted: 'cart.mailErrors.domainDeleted',
  mail_domain_unavailable: 'cart.mailErrors.domainUnavailable',
  domain_held_elsewhere: 'cart.mailErrors.heldElsewhere',
  payment_interval_unavailable: 'cart.checkout.errors.periodUnavailable',
  product_unavailable: 'cart.checkout.errors.productUnavailable',
  vserver_template_unavailable: 'cart.checkout.errors.vserverTemplateUnavailable',
  vserver_hostname_invalid: 'cart.checkout.errors.vserverHostnameInvalid',
  cart_owner_required: 'cart.checkout.errors.cartOwnerRequired',
  cart_too_large: 'cart.checkout.errors.cartTooLarge',
  payment_setup_unavailable: 'cart.checkout.errors.paymentSetupUnavailable',
  terms_version_stale: 'cart.checkout.errors.termsVersionStale',
  consent_required: 'cart.checkout.errors.consentRequired',
  consent_version_required: 'cart.checkout.errors.consentVersionRequired',
  avv_acceptance_required: 'cart.checkout.errors.avvAcceptanceRequired',
  // Domain lines checkout refuses before payment. The first one is also sent
  // as the top-level code beside the per-domain list.
  invalid_domain: 'cart.validation.errors.invalid_domain',
  incompatible_account_type: 'cart.validation.errors.incompatible_account_type',
  missing_handle: 'cart.validation.errors.missing_handle',
  invalid_handle: 'cart.validation.errors.invalid_handle',
  wrong_handle_type: 'cart.validation.errors.wrong_handle_type',
  handle_not_verified: 'cart.validation.errors.handle_not_verified',
  idn_invalid: 'cart.validation.errors.idn_invalid',
  authcode_required: 'cart.validation.errors.authcode_required',
  internal_transfer_authcode_required: 'cart.validation.errors.internal_transfer_authcode_required',
  domain_conflict: 'cart.validation.errors.domain_conflict'
}

/** i18n key for a cart add or checkout refusal the customer can act on. */
export function cartRefusalKey(code: string | undefined): string | undefined {
  return code ? CART_REFUSAL_KEYS[code] : undefined
}

export function useMailBilling() {
  const getBilling = (domainId: string) =>
    useAxios<never, MailBillingStatus>('GET', `/domain/${domainId}/mail/billing`, true)

  // Without a period the backend books monthly; it ignores the period when
  // switching mail back on for a domain that is already entitled.
  const activate = (domainId: string, billingFreq?: PaymentInterval) =>
    useAxios<{ billing_freq: PaymentInterval } | undefined, ActivateResponse>(
      'POST',
      `/domain/${domainId}/mail/activate`,
      true,
      billingFreq ? { billing_freq: billingFreq } : undefined
    )

  const deactivate = (domainId: string) =>
    useAxios<never, ActivateResponse>('POST', `/domain/${domainId}/mail/deactivate`, true)

  return { getBilling, activate, deactivate }
}
